// Fees: our margin on ticket orders and on paid registrations.
//
// Each card is one setting (type + amount, plus who pays for registration)
// and saves on its own. People type percents or DOLLARS; the wire carries a
// percentage (0–100) or integer cents, and raw cents are never shown.
// The worked example follows the SAVED setting; while a draft differs, a
// second "After you save" example sits next to it.

import { useEffect, useMemo, useState } from 'react'
import { api } from '@/api'
import type { FeeSetting, FeeType, RegistrationFeeMode, TenantDetail } from '@/api/types'
import { Badge, Button, Card, CardHeader, FormField, RadioCards, SaveStatus, TextInput, useSaveAction } from '@/components/ui'
import { cn } from '@/lib/format'
import {
  centsToDollars,
  dollarsToCents,
  feeLabel,
  formatPercent,
  parsePercent,
  registrationExample,
  ticketingExample,
  usd,
  EXAMPLE_REGISTRATION_CENTS,
  EXAMPLE_TICKET_ORDER_CENTS,
  type FeeExample,
} from '@/lib/fees'
import { useTenant } from '../tenantContext'

type Kind = 'ticketing' | 'registration'
type Choice = 'default' | FeeType

const COPY: Record<Kind, { title: string; description: string; per: string; of: string; defaultFlat: string }> = {
  ticketing: {
    title: 'Ticketing fee',
    description: 'Our margin on each ticket order. The league absorbs it, along with Stripe’s cut. Cash and comp door sales are billed separately on Stripe.',
    per: 'per order',
    of: 'of each order',
    defaultFlat: '1.00',
  },
  registration: {
    title: 'Registration fee',
    description: 'Our margin on each paid registration, and which side of the transaction pays it.',
    per: 'per registration',
    of: 'of each registration',
    defaultFlat: '2.50',
  },
}

function describe(f: Pick<FeeSetting, 'type' | 'value'>, kind: Kind): string {
  return f.type === 'percentage' ? `${formatPercent(f.value)}% ${COPY[kind].of}` : feeLabel(f, COPY[kind].per)
}

export default function FeesTab() {
  const { tenant } = useTenant()
  return (
    <div className="grid items-start gap-4 xl:grid-cols-2">
      <FeeCard kind="ticketing" tenant={tenant} />
      <FeeCard kind="registration" tenant={tenant} />
    </div>
  )
}

interface Draft {
  choice: Choice
  percent: string
  dollars: string
  mode: RegistrationFeeMode
}

function draftFrom(saved: FeeSetting & { mode?: RegistrationFeeMode }, platformDefault: Pick<FeeSetting, 'type' | 'value'>, kind: Kind): Draft {
  return {
    choice: saved.is_default ? 'default' : saved.type,
    percent: !saved.is_default && saved.type === 'percentage' ? formatPercent(saved.value) : platformDefault.type === 'percentage' ? formatPercent(platformDefault.value) : '',
    dollars: !saved.is_default && saved.type === 'flat' ? centsToDollars(saved.value) : COPY[kind].defaultFlat,
    mode: saved.mode ?? 'passed_on',
  }
}

function FeeCard({ kind, tenant }: { kind: Kind; tenant: TenantDetail }) {
  const { apply } = useTenant()
  const saved = tenant.fees[kind] as FeeSetting & { mode?: RegistrationFeeMode }
  const platformDefault = tenant.fees.platform_defaults[kind] as Pick<FeeSetting, 'type' | 'value'> & { mode?: RegistrationFeeMode }
  const savedKey = JSON.stringify([saved.is_default, saved.type, saved.value, saved.mode ?? null])
  const [draft, setDraft] = useState<Draft>(() => draftFrom(saved, platformDefault, kind))
  const save = useSaveAction()

  // Adopt the server's value after a save (or another tab's change).
  useEffect(() => {
    setDraft(draftFrom(saved, platformDefault, kind))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey])

  const parsed = useMemo((): { ok: true; type: FeeType | null; value: number | null } | { ok: false; error: string } => {
    if (draft.choice === 'default') return { ok: true, type: null, value: null }
    if (draft.choice === 'percentage') {
      const n = parsePercent(draft.percent)
      return n === null ? { ok: false, error: 'Enter a percentage from 0 to 100, with up to 2 decimals.' } : { ok: true, type: 'percentage', value: n }
    }
    const cents = dollarsToCents(draft.dollars)
    return cents === null ? { ok: false, error: 'Enter a dollar amount, e.g. 1.50.' } : { ok: true, type: 'flat', value: cents }
  }, [draft])

  const draftFee = parsed.ok ? (parsed.type ? { type: parsed.type, value: parsed.value! } : { type: platformDefault.type, value: platformDefault.value }) : null
  const draftKey = parsed.ok ? JSON.stringify([parsed.type === null, parsed.type ?? platformDefault.type, parsed.value ?? platformDefault.value, kind === 'registration' ? draft.mode : null]) : null
  const savedCmpKey = JSON.stringify([saved.is_default, saved.is_default ? platformDefault.type : saved.type, saved.is_default ? platformDefault.value : saved.value, kind === 'registration' ? saved.mode ?? 'passed_on' : null])
  const dirty = draftKey !== savedCmpKey
  const [showErrors, setShowErrors] = useState(false)

  const example = (fee: { type: FeeType; value: number }, mode: RegistrationFeeMode) => (kind === 'ticketing' ? ticketingExample(fee) : registrationExample(fee, mode))
  const savedEffective = { type: saved.type, value: saved.value }
  const savedExample = example(savedEffective, saved.mode ?? 'passed_on')
  const draftExample = draftFee && dirty ? example(draftFee, draft.mode) : null

  const submit = async () => {
    if (!parsed.ok) {
      setShowErrors(true)
      return
    }
    setShowErrors(false)
    const body = { type: parsed.type, value: parsed.value }
    await save.run(async () => {
      const t =
        kind === 'ticketing'
          ? await api.tenants.setTicketingFee(tenant.id, body)
          : await api.tenants.setRegistrationFee(tenant.id, { ...body, mode: draft.mode })
      apply(t)
    })
  }

  const defaultLabel = `Platform default (${describe(platformDefault, kind)})`
  const amountError = showErrors && !parsed.ok ? parsed.error : null

  return (
    <Card>
      <CardHeader title={COPY[kind].title} description={COPY[kind].description} />
      <p className="mb-4 text-[13px]">
        <span className="text-muted">Now: </span>
        <span className="font-semibold">{describe(saved, kind)}</span>
        {kind === 'registration' ? <span className="text-muted">, {saved.mode === 'absorbed' ? 'league absorbs it' : 'registrant pays it'}</span> : null}{' '}
        {saved.is_default ? <Badge tone="outline">Platform default</Badge> : <Badge tone="accent">Override</Badge>}
      </p>

      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        {kind === 'registration' ? (
          <RadioCards<RegistrationFeeMode>
            legend="Who pays the fee"
            value={draft.mode}
            onChange={(mode) => setDraft((d) => ({ ...d, mode }))}
            columns={2}
            options={[
              { value: 'passed_on', label: 'Registrant pays', description: 'Their total is grossed up to cover Stripe’s cut and our margin. The league receives the full price.' },
              { value: 'absorbed', label: 'League absorbs', description: 'The registrant pays the sticker price; both cuts come out of the league’s proceeds, like ticketing.' },
            ]}
          />
        ) : null}

        <RadioCards<Choice>
          legend="Rate"
          value={draft.choice}
          onChange={(choice) => {
            setShowErrors(false)
            setDraft((d) => ({ ...d, choice }))
          }}
          columns={3}
          options={[
            { value: 'default', label: 'Platform default', description: describe(platformDefault, kind) },
            { value: 'percentage', label: 'Percentage', description: `A share ${COPY[kind].of}` },
            { value: 'flat', label: 'Flat fee', description: `A fixed amount ${COPY[kind].per}` },
          ]}
        />

        {draft.choice === 'percentage' ? (
          <FormField label="Percentage" error={amountError} hint={`0–100, up to 2 decimals. Taken ${COPY[kind].of}.`}>
            {({ id, describedBy, invalid }) => (
              <div className="relative max-w-[10rem]">
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  inputMode="decimal"
                  value={draft.percent}
                  onChange={(e) => setDraft((d) => ({ ...d, percent: e.target.value }))}
                  className="pr-8 tabular"
                />
                <span aria-hidden className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-muted">
                  %
                </span>
              </div>
            )}
          </FormField>
        ) : draft.choice === 'flat' ? (
          <FormField label="Amount in dollars" error={amountError} hint={`Charged ${COPY[kind].per}, capped at the amount paid.`}>
            {({ id, describedBy, invalid }) => (
              <div className="relative max-w-[10rem]">
                <span aria-hidden className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted">
                  $
                </span>
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  inputMode="decimal"
                  value={draft.dollars}
                  onChange={(e) => setDraft((d) => ({ ...d, dollars: e.target.value }))}
                  className="pl-7 tabular"
                />
              </div>
            )}
          </FormField>
        ) : (
          <p className="text-xs text-muted">{defaultLabel}. Changes to the platform default apply to this league automatically.</p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <ExampleBox title={`Saved: a ${usd(kind === 'ticketing' ? EXAMPLE_TICKET_ORDER_CENTS : EXAMPLE_REGISTRATION_CENTS)} ${kind === 'ticketing' ? 'order' : 'registration'}`} ex={savedExample} />
          {draftExample ? <ExampleBox title="After you save" ex={draftExample} highlight /> : null}
        </div>
        <p className="-mt-1 text-2xs text-subtle">Stripe’s share is estimated at a standard US card rate (2.9% + 30¢). Real charges come from the server.</p>

        <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
          <Button type="submit" variant="primary" disabled={!dirty || save.status === 'saving'} loading={save.status === 'saving'}>
            Save {kind} fee
          </Button>
          {dirty ? (
            <Button
              variant="ghost"
              onClick={() => {
                setShowErrors(false)
                setDraft(draftFrom(saved, platformDefault, kind))
                save.reset()
              }}
            >
              Discard
            </Button>
          ) : null}
          <SaveStatus state={save} onRetry={submit} />
        </div>
      </form>
    </Card>
  )
}

function ExampleBox({ title, ex, highlight }: { title: string; ex: FeeExample; highlight?: boolean }) {
  const rows: Array<[string, string, boolean?]> = [
    ['Fan pays', usd(ex.fanPays)],
    ['Stripe takes (est.)', `−${usd(ex.stripe)}`],
    ['We take', `−${usd(ex.platform)}`],
    ['League nets', usd(ex.leagueNets), true],
  ]
  return (
    <figure className={cn('m-0 rounded-md border p-3', highlight ? 'border-accent/50 bg-accent-soft/30' : 'border-line bg-canvas/40')}>
      <figcaption className="mb-2 text-xs font-semibold text-ink">{title}</figcaption>
      <dl className="grid gap-1 text-[13px]">
        {rows.map(([label, value, total]) => (
          <div key={label} className={cn('flex justify-between gap-3', total && 'mt-0.5 border-t border-line pt-1.5 font-semibold')}>
            <dt className={total ? 'text-ink' : 'text-muted'}>{label}</dt>
            <dd className="font-mono tabular text-ink">{value}</dd>
          </div>
        ))}
      </dl>
    </figure>
  )
}
